'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

export type BiometricCaptureState = 'searching' | 'detected' | 'ready';
export type ExtendedBiometricCaptureState = BiometricCaptureState | 'move_closer' | 'move_back' | 'hold_still' | 'improve_lighting' | 'resetting';

interface UseStableBiometricCaptureStateOptions {
  rawState: ExtendedBiometricCaptureState;
  message: string;
  readyHoldMs?: number;
  detectedHoldMs?: number;
}

const STATE_RANK: Record<ExtendedBiometricCaptureState, number> = {
  searching: 0,
  detected: 1,
  move_closer: 1,
  move_back: 1,
  hold_still: 1,
  improve_lighting: 1,
  resetting: 1,
  ready: 2,
};

function getMinimumDisplayTime(
  state: ExtendedBiometricCaptureState,
  readyHoldMs: number,
  detectedHoldMs: number,
): number {
  if (state === 'ready') {
    return readyHoldMs;
  }

  if (state === 'detected') {
    return detectedHoldMs;
  }

  return 0;
}

export function useStableBiometricCaptureState({
  rawState,
  message,
  readyHoldMs = 2500,
  detectedHoldMs = 900,
}: UseStableBiometricCaptureStateOptions) {
  const [displayState, setDisplayState] = useState<ExtendedBiometricCaptureState>(rawState);
  const [displayMessage, setDisplayMessage] = useState(message);
  const stateEnteredAtRef = useRef<number>(Date.now());
  const pendingTransitionRef = useRef<number | null>(null);

  useEffect(() => {
    const clearPendingTransition = () => {
      if (pendingTransitionRef.current !== null) {
        window.clearTimeout(pendingTransitionRef.current);
        pendingTransitionRef.current = null;
      }
    };

    const nextIsUpgrade = STATE_RANK[rawState] >= STATE_RANK[displayState];

    if (nextIsUpgrade) {
      clearPendingTransition();

      if (displayState !== rawState) {
        setDisplayState(rawState);
        stateEnteredAtRef.current = Date.now();
      }

      setDisplayMessage(message);
      return clearPendingTransition;
    }

    const minDisplayTime = getMinimumDisplayTime(displayState, readyHoldMs, detectedHoldMs);
    const elapsed = Date.now() - stateEnteredAtRef.current;

    if (elapsed >= minDisplayTime) {
      clearPendingTransition();
      setDisplayState(rawState);
      setDisplayMessage(message);
      stateEnteredAtRef.current = Date.now();
      return clearPendingTransition;
    }

    clearPendingTransition();
    pendingTransitionRef.current = window.setTimeout(() => {
      setDisplayState(rawState);
      setDisplayMessage(message);
      stateEnteredAtRef.current = Date.now();
      pendingTransitionRef.current = null;
    }, minDisplayTime - elapsed);

    return clearPendingTransition;
  }, [detectedHoldMs, displayState, message, rawState, readyHoldMs]);

  return useMemo(() => ({
    displayState,
    displayMessage,
    displayDetected: displayState !== 'searching',
    displayReady: displayState === 'ready',
  }), [displayMessage, displayState]);
}
